import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, X } from 'react-feather';
import { APP_VERSION } from '../config/version';

const DISMISS_KEY          = 'app_update_dismissed';
const DISMISS_VERSION_KEY  = 'app_update_dismissed_version';

if (typeof document !== 'undefined' && !document.getElementById('app-update-banner-styles')) {
  const s = document.createElement('style');
  s.id = 'app-update-banner-styles';
  s.textContent = `
    @keyframes au-in {
      from { opacity: 0; transform: translateY(-100%); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes au-out {
      from { opacity: 1; transform: translateY(0); }
      to   { opacity: 0; transform: translateY(-100%); }
    }
    @keyframes au-spin {
      to { transform: rotate(360deg); }
    }
    @keyframes au-sweep {
      0%   { transform: scaleX(0); }
      100% { transform: scaleX(1); }
    }
    @keyframes au-dot {
      0%, 80%, 100% { transform: scale(0.5); opacity: 0.3; }
      40%           { transform: scale(1);   opacity: 1;   }
    }
    @keyframes au-pulse-ring {
      0%  { transform: scale(1);    opacity: 0.7; }
      70% { transform: scale(2);    opacity: 0;   }
      100%{ transform: scale(2);    opacity: 0;   }
    }

    .au-wrap {
      position: fixed;
      top: 0; left: 0; right: 0;
      z-index: 99999;
      /* near-black — contrasts both white content and green sidebar */
      background: #111827;
      box-shadow: 0 2px 12px rgba(0,0,0,0.35);
      animation: au-in 0.38s cubic-bezier(0.16, 1, 0.3, 1) both;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
    .au-wrap.au-out {
      animation: au-out 0.28s cubic-bezier(0.4, 0, 1, 1) both;
    }

    /* green sweep line at bottom — subtle brand tie-in */
    .au-line {
      position: absolute;
      bottom: 0; left: 0; right: 0;
      height: 2px;
      background: linear-gradient(90deg, #009342 0%, #00c85a 50%, #009342 100%);
      background-size: 200% auto;
      transform-origin: left;
      animation: au-sweep 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.15s both;
    }

    /* loading version of line */
    .au-line-loading {
      position: absolute;
      bottom: 0; left: 0; right: 0;
      height: 2px;
      background: linear-gradient(90deg, transparent, #00c85a, transparent);
      background-size: 60% auto;
      animation: au-shimmer-line 1.2s linear infinite;
    }
    @keyframes au-shimmer-line {
      0%   { background-position: -100% center; }
      100% { background-position:  200% center; }
    }

    .au-body {
      display: flex;
      align-items: center;
      padding: 11px 20px;
      gap: 16px;
      max-width: 1400px;
      margin: 0 auto;
      position: relative;
    }

    /* pulsing live dot */
    .au-live-dot-wrap {
      position: relative;
      width: 8px; height: 8px;
      flex-shrink: 0;
    }
    .au-live-dot {
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #4ade80;
      position: relative;
      z-index: 1;
    }
    .au-live-dot-ring {
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: #4ade80;
      animation: au-pulse-ring 1.8s ease-out infinite;
    }

    .au-left {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
      min-width: 0;
      flex-wrap: wrap;
    }

    .au-badge {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.8px;
      text-transform: uppercase;
      color: #4ade80;
      background: rgba(74,222,128,0.12);
      border: 1px solid rgba(74,222,128,0.25);
      padding: 2px 8px;
      border-radius: 4px;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .au-msg {
      font-size: 13px;
      color: rgba(255,255,255,0.75);
      font-weight: 400;
      line-height: 1.4;
      min-width: 0;
    }
    .au-msg strong {
      color: #ffffff;
      font-weight: 600;
    }
    .au-chip {
      display: inline-block;
      font-size: 11px;
      font-weight: 600;
      font-family: 'SF Mono', 'Fira Code', 'Menlo', monospace;
      background: rgba(255,255,255,0.08);
      border: 1px solid rgba(255,255,255,0.14);
      color: rgba(255,255,255,0.6);
      padding: 1px 6px;
      border-radius: 4px;
      margin: 0 2px;
      vertical-align: baseline;
    }
    .au-chip-new {
      background: rgba(74,222,128,0.1);
      border-color: rgba(74,222,128,0.25);
      color: #86efac;
    }

    .au-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .au-btn {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      background: #ffffff;
      color: #111827;
      border: none;
      padding: 7px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
      letter-spacing: 0.1px;
      transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
    .au-btn:hover:not(:disabled) {
      background: #f3f4f6;
      transform: translateY(-1px);
      box-shadow: 0 3px 10px rgba(0,0,0,0.3);
    }
    .au-btn:active:not(:disabled) {
      transform: translateY(0);
      background: #e5e7eb;
    }
    .au-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .au-spin-icon { animation: au-spin 0.8s linear infinite; }

    .au-dots {
      display: inline-flex;
      gap: 3px;
      align-items: center;
    }
    .au-dots span {
      display: block;
      width: 3px; height: 3px;
      border-radius: 50%;
      background: currentColor;
      animation: au-dot 1.2s ease-in-out infinite;
    }
    .au-dots span:nth-child(2) { animation-delay: 0.15s; }
    .au-dots span:nth-child(3) { animation-delay: 0.30s; }

    .au-close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px; height: 28px;
      background: transparent;
      border: 1px solid rgba(255,255,255,0.15);
      border-radius: 6px;
      color: rgba(255,255,255,0.45);
      cursor: pointer;
      transition: background 0.15s, color 0.15s, border-color 0.15s;
      flex-shrink: 0;
    }
    .au-close:hover:not(:disabled) {
      background: rgba(255,255,255,0.1);
      border-color: rgba(255,255,255,0.3);
      color: rgba(255,255,255,0.9);
    }
    .au-close:disabled { opacity: 0.3; cursor: not-allowed; }

    @media (max-width: 640px) {
      .au-body { gap: 10px; padding: 9px 14px; }
      .au-msg  { font-size: 12px; }
      .au-chip { display: none; }
    }
  `;
  document.head.appendChild(s);
}

const AppUpdateBanner = () => {
  const [visible, setVisible]       = useState(false);
  const [hiding, setHiding]         = useState(false);
  const [serverVersion, setServer]  = useState(null);
  const [isReloading, setReloading] = useState(false);
  const timer = useRef(null);

  const shouldShow = useCallback((version) => {
    const dv = localStorage.getItem(DISMISS_VERSION_KEY);
    const dt = localStorage.getItem(DISMISS_KEY);
    if (dv !== version) return true;
    if (!dt) return true;
    return Date.now() - parseInt(dt, 10) > 60 * 1000; // 1 minute
  }, []);

  const hide = useCallback((cb) => {
    setHiding(true);
    timer.current = setTimeout(() => {
      setVisible(false);
      setHiding(false);
      if (cb) cb();
    }, 300);
  }, []);

  useEffect(() => {
    const onMismatch = (e) => {
      const v = e.detail.serverVersion;
      if (shouldShow(v)) { setServer(v); setVisible(true); setHiding(false); }
    };
    const onMatch = () => hide(() => {
      setServer(null);
      localStorage.removeItem(DISMISS_KEY);
      localStorage.removeItem(DISMISS_VERSION_KEY);
    });
    window.addEventListener('app-version-mismatch', onMismatch);
    window.addEventListener('app-version-match', onMatch);
    return () => {
      window.removeEventListener('app-version-mismatch', onMismatch);
      window.removeEventListener('app-version-match', onMatch);
      clearTimeout(timer.current);
    };
  }, [shouldShow, hide]);

  const handleReload = async () => {
    setReloading(true);
    localStorage.removeItem(DISMISS_KEY);
    localStorage.removeItem(DISMISS_VERSION_KEY);
    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } catch (_) { /* ignore */ }
    }
    window.location.reload(true);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    localStorage.setItem(DISMISS_VERSION_KEY, serverVersion);
    hide();
  };

  if (!visible) return null;

  return (
    <div className={`au-wrap${hiding ? ' au-out' : ''}`}>
      {isReloading ? <div className="au-line-loading" /> : <div className="au-line" />}

      <div className="au-body">

        <div className="au-live-dot-wrap">
          <div className="au-live-dot-ring" />
          <div className="au-live-dot" />
        </div>

        <div className="au-left">
          <span className="au-badge">Update ready</span>
          <span className="au-msg">
            <strong>New features &amp; bug fixes</strong> are available — you&apos;re on
            <span className="au-chip">v{APP_VERSION}</span>, reload to get
            <span className="au-chip au-chip-new">v{serverVersion}</span>
          </span>
        </div>

        <div className="au-right">
          <button className="au-btn" onClick={handleReload} disabled={isReloading}>
            {isReloading ? (
              <>
                <RefreshCw size={13} className="au-spin-icon" />
                Reloading
                <span className="au-dots"><span /><span /><span /></span>
              </>
            ) : (
              <>
                <RefreshCw size={13} />
                Reload &amp; Update
              </>
            )}
          </button>

          <button
            className="au-close"
            onClick={handleDismiss}
            disabled={isReloading}
            title="Dismiss — will remind again in 1 minute"
          >
            <X size={14} />
          </button>
        </div>

      </div>
    </div>
  );
};

export default AppUpdateBanner;
